// components/common/modal/Modal.tsx
"use client";

import React, { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import Button from "../Button";
import "./Modal.css";

interface ModalProps {
  isOpen: boolean;                    // open/close gate
  onClose: () => void;                // close handler
  title?: string;                     // header title
  message?: React.ReactNode;          // optional message block
  confirmText?: string;               // confirm button label
  cancelText?: string;                // cancel button label
  onConfirm?: () => void;             // confirm handler
  disableBackdropClick?: boolean;     // if true, clicking outside won't close
  isLoading?: boolean;                // loading state for confirm button
  children?: ReactNode;               // custom body content
  hideFooter?: boolean;               // hide default footer (custom actions via children)
  confirmVariant?: "primary" | "danger" | "success" | "warning"; // confirm button style

  /** NEW: disable confirm button (for pattern-confirm, etc.) */
  confirmDisabled?: boolean;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  disableBackdropClick = false,
  isLoading = false,
  children,
  hideFooter,
  confirmVariant = "danger", // default visual intent
  confirmDisabled = false,   // NEW: defaults to false to keep old behavior
}: ModalProps) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    // close only when clicking directly on overlay (not on the dialog)
    if (!disableBackdropClick && e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div
      className="modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
    >
      <div className="modal-container">
        <div className="modal-header">
          {title && (
            <h3 className="modal-title" id="modal-title">
              {title}
            </h3>
          )}
          <button
            className="modal-close-button"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {message &&
            (typeof message === "string" ? (
              <p className="modal-message">{message}</p>
            ) : (
              <div className="modal-message">{message}</div>
            ))}
          {children}
        </div>

        {!hideFooter && (
          <div className="modal-footer modal-footer-actions">
            <Button variant="outline" size="sm" onClick={onClose} type="button">
              {cancelText}
            </Button>
            {onConfirm && (
              <Button
                // honor the requested visual intent
                variant={confirmVariant as any}
                size="sm"
                onClick={onConfirm}
                isLoading={isLoading}
                disabled={confirmDisabled || isLoading} // <-- NEW: gate confirm
                type="button"
              >
                {confirmText}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
